import { useMemo, useState } from "react";
import { color } from "d3-color";
import { keyBy } from "lodash";
import { useSnapshot } from "valtio";
import type { PickingInfo, Color } from "@deck.gl/core";

import { useHasPerfSetting } from "react-migration/domains/nav/components/Modals/DebugModal/performanceSettingsStore";
import { useSiteStages } from "react-migration/domains/sites/hooks/useSiteStages";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { useMapLayer } from "react-migration/lib/map/useMapLayer";
import { mapStore } from "src/js/stores/map/store";
import { siteGeometryStore } from "src/js/stores/siteGeometry/store";
import { ENVIRONMENT } from "src/js/util/environment";
import { LayerTypeMapLayerProps } from "../../types";
import { SITES_MAP_LAYER_ID } from "./constants";
import { SitesGeoJsonLayer, SitesMvtLayer } from "./SitesGeoJsonLayer";
import { ClusterFeature } from "./types";
import { useFeatureIsVisible } from "./useFeatureIsVisible";
import { isSiteBoundary } from "./utils";
import { SitesLayer } from ".";

const TRANSPARENT: Color = [0, 0, 0, 0];
const CLUSTER_COLOR: Color = [252, 254, 7];

export function SitesMapLayer(props: LayerTypeMapLayerProps) {
  const hasSitesMvtLayer = useHasPerfSetting("enableMVTSites");

  if (hasSitesMvtLayer) {
    return <SitesMapLayerMvt {...props} />;
  } else {
    return <SitesMapLayerGeoJson {...props} />;
  }
}

function SitesMapLayerMvt({ zOrder, visible }: LayerTypeMapLayerProps) {
  const { stages } = useSiteStages();
  const { changeCount } = useSnapshot(siteGeometryStore);
  const {
    settings: { showHiddenSites, showSharedSites },
  } = useSnapshot(mapStore);

  const stageColourLookup = useMemo(() => {
    return keyBy(stages, "_id");
  }, [stages]);

  useMapLayer(
    () => ({
      layer: new SitesMvtLayer({
        id: SITES_MAP_LAYER_ID,
        data: `${ENVIRONMENT.SITES_SERVICE_URL}/tiles/{z}/{x}/{y}.mvt?includeHidden=${showHiddenSites}&includeShared=${showSharedSites}`,
        visible,
        filled: true,
        stroked: true,
        pickable: true,
        getFillColor: TRANSPARENT,
        getLineColor: (feature) => {
          if (feature.properties.shared) {
            const { r, g, b } = color("#01EBC7")!.rgb();
            return [r, g, b];
          }

          const { r, g, b } = color(stageColourLookup[feature.properties.stage]!.color)!.rgb();
          return [r, g, b];
        },
        updateTriggers: {
          getLineColor: [stageColourLookup, changeCount, showHiddenSites, showSharedSites],
          getTileData: [changeCount, showHiddenSites, showSharedSites],
        },
        lineWidthUnits: "pixels",
        pointRadiusUnits: "pixels",
        getLineWidth: 3,
        getPointRadius: 7,
        renderSubLayers: (props) => new SitesGeoJsonLayer(props),
      }),
      zOrder,
    }),
    [showHiddenSites, showSharedSites, visible, stageColourLookup, changeCount, zOrder],
    visible
  );

  return null;
}

function SitesMapLayerGeoJson({ zOrder, layer, detailSelection, visible }: LayerTypeMapLayerProps) {
  const { t } = useTranslation();
  const [hoverInfo, setHoverInfo] = useState<PickingInfo<ClusterFeature>>();
  const featureIsVisible = useFeatureIsVisible(detailSelection);

  const captureInternalPolygonClicks = (layer as SitesLayer).layerConfig?.captureInternalPolygonClicks ?? true;
  const { clusters: clusterFeatures, siteFeatures } = useSnapshot(siteGeometryStore);

  const data = useMemo(
    () => [...siteFeatures, ...clusterFeatures].filter(featureIsVisible),
    [featureIsVisible, siteFeatures, clusterFeatures]
  );

  useMapLayer(
    () => ({
      layer: new SitesGeoJsonLayer({
        id: SITES_MAP_LAYER_ID,
        // @ts-expect-error data is valid
        data,
        visible,
        filled: captureInternalPolygonClicks,
        getFillColor: (feature) => {
          if (isSiteBoundary(feature)) return TRANSPARENT;

          // is cluster
          return CLUSTER_COLOR;
        },

        stroked: true,
        getLineColor: (feature) => {
          if (!isSiteBoundary(feature)) return CLUSTER_COLOR;

          const { r, g, b } = color(feature.properties.color)!.rgb();
          return [r, g, b];
        },
        getLineWidth: 3,
        lineWidthUnits: "pixels",
        getPointRadius: 7,
        pickable: true,
        pointRadiusUnits: "pixels",
        onHover: (info) => {
          if (!info.object || isSiteBoundary(info.object)) {
            setHoverInfo(undefined);
          } else {
            setHoverInfo(info as PickingInfo<ClusterFeature>);
          }
        },
      }),
      zOrder,
    }),
    [data, visible, captureInternalPolygonClicks, zOrder],
    visible
  );

  if (!hoverInfo) return null;

  return (
    <div
      className="absolute z-10 p-2 text-xs bg-white rounded-sm pointer-events-none"
      style={{
        left: hoverInfo.x,
        top: hoverInfo.y,
      }}
    >
      {
        // @ts-expect-error object is valid
        hoverInfo.object.properties?.count
          ? // @ts-expect-error object is valid
            t("sites.sites_in_cluster", { count: hoverInfo.object.properties?.count })
          : t("sites.sites_cluster")
      }
    </div>
  );
}
